export interface CheckRunProgressLogEntry {
  timestampUtc: string;
  message: string;
  step?: string;
  level?: string;
  detailsJson?: string;
}

export async function fetchCheckRunProgressLog(checkRunId: string): Promise<CheckRunProgressLogEntry[]> {
  const { data } = await api.get(`/checkruns/${checkRunId}/progress-log`);
  return data;
}

// Products / catalog
export interface ProductDto {
  id: string
  code: string
  name: string
  market?: string
  family?: string
  kingspanUrl?: string
  country?: string
  division?: string
  languageCode?: string
  localeCode?: string
  profileJson?: string | null
  isApproved?: boolean
  approvedBy?: string | null
  approvedUtc?: string | null
  hasKingspanSource?: boolean
  applicationType?: string | null
  variantCount?: number
  lastComplianceCheckUtc?: string | null
}

export interface ProductCatalogItemPayload {
  itemType: 'Keyword' | 'Datasheet' | string
  value: string
  description?: string
  metadataJson?: string
}

export interface ProductCatalogItemDto {
  id: string
  itemType: string
  value: string
  description?: string
  metadataJson?: string
  contentHash?: string
  hashStatus?: string
  hashError?: string
  hashSource?: string
  lastHashedUtc?: string
  contentType?: string
  contentLength?: number
  createdUtc: string
}

export interface ProductListParams {
  family?: string
  market?: string
  division?: string
  search?: string
  approval?: string
  kingspanOnly?: boolean
  distributorId?: string
}

export async function fetchProducts(params?: ProductListParams) {
  const { data } = await api.get<ProductDto[]>('/products', { params: params ?? {} })
  return data
}

export interface ProductImportResult {
  totalRows: number
  created: number
  updated: number
  skipped: number
  errors: { rowNumber: number; code?: string; message: string }[]
}

export async function importProductsFromCsv(file: File, mode: 'append' | 'upsert', filters?: ProductListParams) {
  const formData = new FormData()
  formData.append('file', file)

  const params: any = { mode }
  if (filters?.family) params.family = filters.family
  if (filters?.market) params.market = filters.market
  if (filters?.division) params.division = filters.division
  if (filters?.search) params.search = filters.search
  if (filters?.approval) params.approval = filters.approval
   if (filters?.kingspanOnly) params.kingspanOnly = filters.kingspanOnly
   if (filters?.distributorId) params.distributorId = filters.distributorId

  const { data } = await api.post<ProductImportResult>('/products/import', formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function exportProductsToCsv(filters?: ProductListParams) {
  const params: any = {}
  if (filters?.family) params.family = filters.family
  if (filters?.market) params.market = filters.market
  if (filters?.division) params.division = filters.division
  if (filters?.search) params.search = filters.search
  if (filters?.approval) params.approval = filters.approval
  if (filters?.kingspanOnly) params.kingspanOnly = filters.kingspanOnly
  if (filters?.distributorId) params.distributorId = filters.distributorId

  const response = await api.get('/products/export', {
    params,
    responseType: 'blob',
  })
  return response.data as Blob
}

export interface UnifiedImportColumnMappingDto {
  sourceHeader: string
  sourceColumnIndex: number
  targetKind: 'ProductCode' | 'ProductName' | 'Attribute' | 'Skip' | string
  targetField?: string | null
  displayLabel?: string | null
  category?: string | null
  dataType?: string | null
  unit?: string | null
  isVariantKey: boolean
  confidence: number
  willCreateCanonicalAttribute: boolean
}

export interface UnifiedImportSheetPreviewDto {
  sheetName: string
  isDefaultSelected: boolean
  isRevisionSheet: boolean
  headerRowNumber: number
  dataStartRowNumber: number
  rowCount: number
  columnCount: number
  headers: string[]
  inferredMappings: UnifiedImportColumnMappingDto[]
  sampleRows: string[][]
}

export interface UnifiedProductImportPreviewDto {
  fileName: string
  format: string
  totalRows: number
  selectedDataRows: number
  sheets: UnifiedImportSheetPreviewDto[]
  warnings: string[]
  uploadId?: string
}

export interface UnifiedProductImportResultDto {
  importBatchId: string
  fileName: string
  mode: 'merge' | 'replace' | string
  uploadedBy: string
  owner: string
  securityScope: string
  totalRows: number
  skippedRows: number
  skippedSheets: number
  productsCreated: number
  productsUpdated: number
  variantsCreated: number
  dataItemsCreated: number
  dataItemsUpdated: number
  canonicalAttributesCreated: number
  aliasesCreated: number
  factsSynced: number
  productsApproved: number
  errors: { sheetName?: string | null; rowNumber?: number | null; message: string }[]
}

export interface UnifiedImportMappingRequest {
  sheets: {
    sheetName: string
    selected: boolean
    mappings: UnifiedImportColumnMappingDto[]
  }[]
}

export async function previewUnifiedProductImport(file: File) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post<UnifiedProductImportPreviewDto>('/products/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function importUnifiedProducts(
  file: File,
  mode: 'merge' | 'replace' = 'merge',
  mapping?: UnifiedImportMappingRequest,
  divisionId?: string,
  owner?: string,
  approveProducts?: boolean,
) {
  const formData = new FormData()
  formData.append('file', file)
  if (mapping) formData.append('mappingJson', JSON.stringify(mapping))
  if (owner?.trim()) formData.append('owner', owner.trim())
  const params: Record<string, string> = { mode }
  if (divisionId) params.divisionId = divisionId
  if (approveProducts) params.approveProducts = 'true'
  const { data } = await api.post<UnifiedProductImportResultDto>('/products/import/unified', formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export interface ProductImportJobDto {
  id: string
  status: string
  fileName: string
  mode: string
  owner: string
  scopeLabel: string
  approveProducts: boolean
  totalRows: number
  processedRows: number
  percent: number
  currentProductCode?: string
  currentProductName?: string
  currentStep?: string
  errorMessage?: string
  createdUtc: string
  startedUtc?: string
  completedUtc?: string
  result?: UnifiedProductImportResultDto
}

export async function startUnifiedProductImportJob(
  file: File | null,
  mode: 'merge' | 'replace' = 'merge',
  mapping?: UnifiedImportMappingRequest,
  divisionId?: string,
  owner?: string,
  approveProducts?: boolean,
  uploadId?: string,
  fileName?: string,
) {
  const formData = new FormData()
  if (uploadId) {
    formData.append('uploadId', uploadId)
    if (fileName) formData.append('fileName', fileName)
  } else if (file) {
    formData.append('file', file)
  }
  if (mapping) formData.append('mappingJson', JSON.stringify(mapping))
  if (owner?.trim()) formData.append('owner', owner.trim())
  const params: Record<string, string> = { mode }
  if (divisionId) params.divisionId = divisionId
  if (approveProducts) params.approveProducts = 'true'
  const { data } = await api.post<{ jobId: string; status: string }>('/products/import/jobs', formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function fetchUnifiedProductImportJob(jobId: string) {
  const { data } = await api.get<ProductImportJobDto>(`/products/import/jobs/${jobId}`)
  return data
}

export async function exportUnifiedProducts(params?: {
  format?: 'csv' | 'json'
  search?: string
  family?: string
}) {
  const response = await api.get('/products/export/unified', {
    params: params ?? {},
    responseType: 'blob',
  })
  return response.data as Blob
}

export interface PcrRecordDto {
  id: string
  productId: string
  productVariantId?: string
  version: string
  status: string
  ingestionSource?: string
  approvedBy?: string
  approvedUtc?: string
  createdUtc: string
}

export interface PcrApproveRequest {
  approvedBy: string
  justification?: string
  evidenceReferencesJson?: string
}

export interface PcrRequestApprovalRequest {
  requestedBy: string
  justification?: string
}

export async function fetchPcrRecords(params?: { productId?: string; productVariantId?: string; status?: string }) {
  const { data } = await api.get<PcrRecordDto[]>('/pcr', { params: params ?? {} })
  return data
}

export interface PcrFieldDto {
  canonicalFieldKey: string
  displayName?: string
  rawValue?: string
  normalizedValue?: string
  requiredOnWebsite: boolean
}

export interface PcrFieldsResponseDto {
  pcrId: string
  productId: string
  productVariantId?: string
  version: string
  status: string
  fieldCount: number
  fields: PcrFieldDto[]
}

export async function fetchPcrFields(pcrId: string) {
  const { data } = await api.get<PcrFieldsResponseDto>(`/pcr/${pcrId}/fields`)
  return data
}

export interface VariantFactsSummaryDto {
  productVariantId?: string
  variantLabel: string
  fieldCount: number
  fieldNames: string[]
}

export interface ProductFactsSummaryDto {
  productId: string
  variants: VariantFactsSummaryDto[]
}

export async function fetchProductFactsSummary(productId: string) {
  const { data } = await api.get<ProductFactsSummaryDto>('/pcr/facts-summary', { params: { productId } })
  return data
}

export async function ingestPcrFromUrl(request: {
  productId: string
  sourceUrl: string
  approvedProductName?: string
  approvedProductDescription?: string
  initiatedBy?: string
}) {
  const { data } = await api.post<PcrRecordDto>('/pcr/ingest/url', request)
  return data
}

/**
 * Builds a draft PCR record from the product's current Kingspan-sourced attribute values
 * (the same data shown on the product detail page) - no URL or file needed. This is the
 * normal path since the CSV/manual-entry data already on file *is* the source of truth.
 */
export async function createPcrFromProductAttributes(request: {
  productId: string
  productVariantId?: string
  version?: string
  initiatedBy?: string
}) {
  const { data } = await api.post<PcrRecordDto>('/pcr/create-from-product', request)
  return data
}

export interface VariantPcrReadinessDto {
  productVariantId?: string
  variantLabel: string
  factCount: number
  hasApprovedPcr: boolean
  approvedPcrId?: string
  hasDraftPcr: boolean
  draftPcrId?: string
  draftVersion?: string
}

export interface ProductVariantPcrReadinessDto {
  productId: string
  variants: VariantPcrReadinessDto[]
  missingApprovedCount: number
}

export async function fetchVariantPcrReadiness(productId: string) {
  const { data } = await api.get<ProductVariantPcrReadinessDto>('/pcr/variant-pcr-readiness', { params: { productId } })
  return data
}

export interface CreateDraftsForMissingVariantsResult {
  productId: string
  createdDrafts: PcrRecordDto[]
  existingDrafts: PcrRecordDto[]
  skippedVariants: string[]
  errors: string[]
}

export async function createDraftPcrsForMissingVariants(request: {
  productId: string
  initiatedBy?: string
}) {
  const { data } = await api.post<CreateDraftsForMissingVariantsResult>('/pcr/create-drafts-for-missing-variants', request)
  return data
}

export async function createAndApproveAllVariantPcrs(request: {
  productId: string
  initiatedBy?: string
  justification?: string
}) {
  const { data } = await api.post<{
    productId: string
    createdOrApproved: PcrRecordDto[]
    skipped: string[]
    errors: string[]
  }>('/pcr/create-and-approve-all-variants', request)
  return data
}

export async function requestPcrApproval(id: string, request: PcrRequestApprovalRequest) {
  const { data } = await api.post<{ id: string; status: string }>(`/pcr/${id}/request-approval`, request)
  return data
}

export async function approvePcrRecord(id: string, request: PcrApproveRequest) {
  const { data } = await api.post<{ id: string; status: string; approvedBy?: string; approvedUtc?: string }>(`/pcr/${id}/approve`, request)
  return data
}

export async function fetchProductCatalog(productId: string) {
  const { data } = await api.get<ProductCatalogItemDto[]>(`/products/${productId}/catalog`)
  return data
}

/** Enqueue a background job to scrape the product's Kingspan URL and populate PIM data. */
export async function enqueueKingspanScrape(productId: string) {
  const { data } = await api.post<{ message?: string }>(`/products/${productId}/kingspan-scrape`)
  return data
}

export async function uploadProductCatalog(productId: string, items: ProductCatalogItemPayload[]) {
  const { data } = await api.post(`/products/${productId}/catalog`, { items })
  return data
}

export async function setCatalogItemHash(productId: string, itemId: string, request: {
  contentHash: string
  contentType?: string
  contentLength?: number
}) {
  const { data } = await api.post(`/products/${productId}/catalog/${itemId}/set-hash`, request)
  return data
}

export async function computeCatalogItemHash(productId: string, itemId: string) {
  const { data } = await api.post(`/products/${productId}/catalog/${itemId}/compute-hash`)
  return data
}

export async function computeAllCatalogHashes(productId: string) {
  const { data } = await api.post(`/products/${productId}/catalog/compute-hashes`)
  return data
}


export async function createProduct(request: {
  code: string
  name: string
  market?: string
  family?: string
  kingspanUrl?: string
  country?: string
  division?: string
  languageCode?: string
  localeCode?: string
}) {
  const { data } = await api.post<ProductDto>('/products', request)
  return data
}


export async function updateProduct(id: string, request: {
  code?: string
  name?: string
  market?: string
  family?: string
  kingspanUrl?: string
  country?: string
  division?: string
  languageCode?: string
  localeCode?: string
}) {
  const { data } = await api.put<ProductDto>(`/products/${id}`, request)
  return data
}

export interface MergePlaceholderProductsRequest {
  productIds: string[]
  destinationProductId?: string
  destinationCode?: string
  destinationName?: string
  kingspanUrl?: string
  market?: string
  family?: string
  country?: string
  division?: string
}

export interface MergePlaceholderProductsResponse {
  destinationProductId: string
  destinationCode: string
  destinationName: string
  mergedProductCount: number
}

export async function mergePlaceholderProducts(request: MergePlaceholderProductsRequest) {
  const { data } = await api.post<MergePlaceholderProductsResponse>('/products/merge-placeholders', request)
  return data
}

export async function deleteProduct(id: string) {
  await api.delete(`/products/${id}`)
}

export interface ProductPreviewDto {
  code: string
  name: string
  kingspanUrl: string
  country?: string
  division?: string
  market?: string
  family?: string
}

export async function extractProductFromUrl(url: string) {
  const { data } = await api.post<ProductPreviewDto>('/products/extract-from-url', { url })
  return data
}

export interface ProductDataItemDto {
  id: string
  category: string
  attributeName: string
  value: string
  unit?: string
  displayLabel?: string
  dataType?: string
  source: string
  sourceUrl?: string
  languageCode?: string
  localeCode?: string
  rawContentBlobUri?: string
  contentHash?: string
  scrapedUtc: string
  targetId?: string
  isSourceOfTruth: boolean
  sourceOfTruthApprovedBy?: string
  sourceOfTruthApprovedUtc?: string
  productVariantId?: string
  variantLabel?: string
}

export interface ProductPimEvidenceDto {
  itemId: string
  productId: string
  targetId?: string
  category: string
  attributeName: string
  displayLabel?: string
  value: string
  unit?: string
  source: string
  sourceUrl?: string
  scrapedUtc: string
  extractionMethod?: string
  confidence?: number
  extractionMetadataJson?: string
  rawContentBlobUri?: string
  rawContentBlobName?: string
  valueOccurrencesInEvidenceText: number
  isGroundedInEvidenceText: boolean
  evidenceSnippet?: string
  evidenceText?: string
  evidenceHighlightedHtml?: string
  retrievalError?: string
}

export interface ProductTargetDto {
  targetId: string
  url: string
  description?: string
  contentType?: string
  urlCategory?: string
  healthStatus?: string
  lastCheckedUtc?: string
  distributorId: string
  distributorName?: string
  distributorDomain?: string
}

export type ProductWebsiteUrlCategory = 'KingspanMarketing' | 'Distributor' | 'OtherSeller'
export type TargetSectionCode = 'Marketing' | 'TechnicalData' | 'Downloads' | 'Other'

export interface ProductWebsiteUrlScheduleDto {
  id: string
  rulePackId: string
  cronExpression: string
  scheduleLabel?: string
  hangfireJobId?: string
  isActive: boolean
}

export interface ProductWebsiteUrlDto {
  id: string
  productId: string
  targetId: string
  url: string
  category: ProductWebsiteUrlCategory
  label?: string
  domain?: string
  ownerId: string
  ownerName?: string
  contentType?: TargetSectionCode | string
  healthStatus?: string
  lastCheckedUtc?: string
  lastCheckRunId?: string
  lastCheckStatus?: string
  findingCount: number
  schedule?: ProductWebsiteUrlScheduleDto | null
  canRunComparison: boolean
}

export interface CreateProductWebsiteUrlRequest {
  category: ProductWebsiteUrlCategory
  url: string
  contentType?: TargetSectionCode | string
  contentTypes?: TargetSectionCode[]
  displayName?: string
  market?: string
  contactEmail?: string
}

export interface CreateProductWebsiteUrlResponse {
  message: string
  targetId: string
  targetUrl: string
  category: ProductWebsiteUrlCategory
  contentType?: TargetSectionCode | string
  ownerId: string
  ownerName?: string
  targetCreated: boolean
}

export interface CreateProductWebsiteUrlScheduleRequest {
  targetIds: string[]
  rulePackId: string
  cronExpression: string
  scheduleLabel?: string
}

export interface CreateProductWebsiteUrlScheduleResponse {
  message: string
  recurringJobId: string
  targetCount: number
}

export interface AddDistributorProductUrlRequest {
  url: string
  distributorName?: string
  market?: string
  approvalStatus?: string
  address?: string
  contactName?: string
  contactEmail?: string
  phone?: string
}

export interface AddDistributorProductUrlResponse {
  message: string
  distributorId: string
  distributorName: string
  distributorDomain: string
  targetId: string
  targetUrl: string
  distributorCreated: boolean
  targetCreated: boolean
}

export async function fetchProductPimData(productId: string) {
  const { data } = await api.get<ProductDataItemDto[]>(`/products/${productId}/pim-data`)
  return data
}

export async function clearKingspanPimData(productId: string) {
  const { data } = await api.delete<{ message: string; removedDataItems: number; archivedFacts: number }>(`/products/${productId}/pim-data/kingspan`)
  return data
}

export async function clearDistributorPimData(productId: string, distributorId?: string) {
  const { data } = await api.delete<{ message: string; removedDataItems: number; archivedFacts: number }>(`/products/${productId}/pim-data/distributor`, {
    params: distributorId ? { distributorId } : {},
  })
  return data
}

export async function fetchProductPimEvidence(productId: string, itemId: string) {
  const { data } = await api.get<ProductPimEvidenceDto>(`/products/${productId}/pim-data/${itemId}/evidence`)
  return data
}

export async function updateProductPimField(
  productId: string,
  itemId: string,
  request: { value?: string; unit?: string | null; displayLabel?: string | null; dataType?: string | null }
) {
  const { data } = await api.put(`/products/${productId}/pim-data/${itemId}`, {
    value: request.value,
    unit: request.unit ?? null,
    unitHasValue: request.unit !== undefined,
    displayLabel: request.displayLabel ?? null,
    displayLabelHasValue: request.displayLabel !== undefined,
    dataType: request.dataType ?? null,
    dataTypeHasValue: request.dataType !== undefined,
  })
  return data
}

export async function setProductPimFieldSourceOfTruth(productId: string, itemId: string, isSourceOfTruth: boolean) {
  const { data } = await api.post(`/products/${productId}/pim-data/${itemId}/source-of-truth`, {
    isSourceOfTruth,
  })
  return data
}

export async function setProductApproval(productId: string, isApproved: boolean) {
  const { data } = await api.post(`/products/${productId}/approve`, { isApproved })
  return data
}

export async function fetchProductTargets(productId: string) {
  const { data } = await api.get<ProductTargetDto[]>(`/products/${productId}/targets`)
  return data
}

export async function fetchProductWebsiteUrls(productId: string) {
  const { data } = await api.get<ProductWebsiteUrlDto[]>(`/products/${productId}/website-urls`)
  return data
}

export async function addProductWebsiteUrl(productId: string, request: CreateProductWebsiteUrlRequest) {
  const { data } = await api.post<CreateProductWebsiteUrlResponse>(`/products/${productId}/website-urls`, request)
  return data
}

export async function scheduleProductWebsiteUrls(productId: string, request: CreateProductWebsiteUrlScheduleRequest) {
  const { data } = await api.post<CreateProductWebsiteUrlScheduleResponse>(`/products/${productId}/website-url-schedules`, request)
  return data
}

export interface ProductVariantDto {
  id: string
  variantCode?: string
  variantKeysJson?: string
  lifecycleStatus?: string
}

export async function fetchProductVariants(productId: string) {
  const { data } = await api.get<ProductVariantDto[]>(`/products/${productId}/variants`)
  return data
}

export interface ThermalConductivityRuleDto {
  minThicknessMm: number
  maxThicknessMm?: number | null
  value: string
  unit?: string
}

export interface VariantMatrixRowDto {
  id?: string
  thicknessMm?: number
  thermalConductivity?: string
  pricePerM2?: number
  boardsPerPack?: number
  boardsPerPallet?: number
  weightKgPerM2?: number
  m2PerPack?: number
  lifecycleStatus?: string
}

export interface VariantMatrixDto {
  conductivityRules: ThermalConductivityRuleDto[]
  variants: VariantMatrixRowDto[]
}

export async function fetchVariantMatrix(productId: string) {
  const { data } = await api.get<VariantMatrixDto>(`/products/${productId}/variant-matrix`)
  return data
}

export async function saveVariantMatrix(productId: string, payload: VariantMatrixDto) {
  const { data } = await api.put<VariantMatrixDto>(`/products/${productId}/variant-matrix`, payload)
  return data
}

export async function addDistributorUrlToProduct(productId: string, request: AddDistributorProductUrlRequest) {
  const { data } = await api.post<AddDistributorProductUrlResponse>(`/products/${productId}/distributor-url`, request)
  return data
}

export async function removeDistributorUrlFromProduct(productId: string, targetId: string) {
  const { data } = await api.delete<{ message: string }>(`/products/${productId}/distributor-url/${targetId}`)
  return data
}

export async function deleteProductCatalogItem(productId: string, itemId: string) {
  await api.delete(`/products/${productId}/catalog/${itemId}`)
}

export interface ProductComparisonRowDto {
  canonicalAttributeId: string
  category: string
  attributeName: string
  displayLabel: string
  compareMode?: 'Exact' | 'Pattern' | 'Semantic' | string
  canonicalUnit?: string
  kingspanStatus: 'Present' | 'Missing'
  kingspanValue?: string
  distributorStatus: 'Present' | 'Missing' | 'Conflict'
  distributorValue?: string
  distributorValues: Array<{
    targetId?: string
    url?: string
    value?: string
    unit?: string
  }>
  reviewStatus?: 'Pass' | 'Accepted' | 'Checked' | 'NeedsReview' | 'Finding'
  reviewReason?: string
  humanReviewStatus?: 'Pass' | 'Finding'
  humanReviewReason?: string
  humanReviewActor?: string
  humanReviewUtc?: string
  reviewEvidenceQuote?: string
  reviewEvidenceSourceUrl?: string
  needsKingspanUpdate: boolean
  aiPairwiseVerdict?: string
  aiPairwiseConfidence?: number
  aiPairwiseSummary?: string
  aiPairwiseOverrodeFinding?: boolean
}

export interface ProductCompletionQueueItemDto {
  canonicalAttributeId: string
  displayLabel: string
  attributeName: string
  category: string
  status: string
  actionId?: string
  ownerRole: string
  createdUtc: string
}

export interface ProductCompletenessSummaryDto {
  missingKingspanCount: number
  totalCanonicalCount: number
}

export interface ProductFieldRelevanceRuleDto {
  id: string
  productFamily: string
  category: string
  fieldName: string
  sourceScope: string
  isIrrelevant: boolean
  decisionSource: string
  reason?: string
  updatedUtc: string
}

export interface ProductFieldMatchRuleDto {
  id: string
  productFamily: string
  category: string
  kingspanFieldName: string
  distributorFieldName: string
  isActive: boolean
  updatedUtc: string
}

export interface ProductPromptLabRowDto {
  field: string
  found: boolean
  value?: string
  evidenceQuote?: string
  confidence?: number
  note?: string
}

export interface ProductPromptLabVariantResultDto {
  name: string
  model: string
  systemPrompt: string
  userPrompt: string
  rawResponse: string
  rows: ProductPromptLabRowDto[]
}

export interface ProductPromptLabResponseDto {
  productId: string
  targetId: string
  targetUrl?: string
  rawContentBlobUri?: string
  dataSource: string
  dataSourceNote?: string
  rawContentChars: number
  inputCharsSent: number
  fields: string[]
  variants: ProductPromptLabVariantResultDto[]
}

export async function fetchProductComparisonTable(productId: string, distributorId?: string, persistFindings = false) {
  const { data } = await api.get<ProductComparisonRowDto[]>(`/products/${productId}/comparison-table`, {
    params: {
      ...(distributorId ? { distributorId } : {}),
      persistFindings,
    },
  })
  return data
}

export async function recordComparisonHumanDecision(
  productId: string,
  payload: {
    canonicalAttributeId: string
    distributorId?: string
    displayLabel?: string
    compareMode?: string
    kingspanValue?: string
    distributorValue?: string
    decision: 'Pass' | 'Fail'
    rationale?: string
  },
) {
  const { data } = await api.post(`/products/${productId}/comparison-decisions`, payload)
  return data
}

export interface ProductMonitoredAttributeRowDto {
  canonicalAttributeId: string
  name: string
  displayLabel: string
  category: string
  monitored: boolean
}

export interface ProductMonitoredAttributesResponseDto {
  useCustomMonitoredAttributes: boolean
  rows: ProductMonitoredAttributeRowDto[]
}

export async function fetchProductMonitoredAttributes(productId: string) {
  const { data } = await api.get<ProductMonitoredAttributesResponseDto>(`/products/${productId}/monitored-attributes`)
  return data
}

export async function updateProductMonitoredAttributes(
  productId: string,
  body: { useCustomMonitoredAttributes: boolean; canonicalAttributeIds?: string[] },
) {
  const { data } = await api.put<{ message: string; count?: number }>(
    `/products/${productId}/monitored-attributes`,
    body,
  )
  return data
}

export async function enqueuePairwiseAiReconciliation(productId: string, targetId: string) {
  const { data } = await api.post<{ message: string }>(
    `/products/${productId}/targets/${targetId}/pairwise-ai-reconciliation`,
  )
  return data
}

export async function fetchProductCompletionQueue(productId: string) {
  const { data } = await api.get<ProductCompletionQueueItemDto[]>(`/products/${productId}/completion-queue`)
  return data
}

export async function fetchProductCompletenessSummary(productId: string) {
  const { data } = await api.get<ProductCompletenessSummaryDto>(`/products/${productId}/completeness-summary`)
  return data
}

export async function setKingspanCanonicalValue(productId: string, canonicalAttributeId: string, request: { value: string; unit?: string; actor?: string }) {
  const { data } = await api.post<{ message: string }>(`/products/${productId}/canonical-attributes/${canonicalAttributeId}/kingspan-value`, request)
  return data
}

export async function addProductAttribute(productId: string, request: {
  canonicalTerm: string
  category?: string
  dataType?: string
  value: string
  unit?: string
  compareMode?: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion?: boolean
  actor?: string
}) {
  const { data } = await api.post<{ message: string; canonicalAttributeId: string }>(`/products/${productId}/attributes`, request)
  return data
}

export async function syncProductAttributesToMasterData(productId: string, request?: { actor?: string }) {
  const { data } = await api.post<{
    message: string
    attributesChecked: number
    masterTermsCreated: number
    familyLinksCreated: number
  }>(`/products/${productId}/attributes/sync-master-data`, request ?? {})
  return data
}

export async function deleteProductAttribute(productId: string, canonicalAttributeId: string, deleteFromFamily = false) {
  const { data } = await api.delete<{ message: string }>(`/products/${productId}/attributes/${canonicalAttributeId}`, {
    params: { deleteFromFamily },
  })
  return data
}

export async function clearKingspanCanonicalValue(productId: string, canonicalAttributeId: string) {
  const { data } = await api.delete<{ message: string }>(`/products/${productId}/canonical-attributes/${canonicalAttributeId}/kingspan-value`)
  return data
}

export async function fetchProductFieldRelevanceRules(productId: string) {
  const { data } = await api.get<ProductFieldRelevanceRuleDto[]>(`/products/${productId}/field-relevance-rules`)
  return data
}

export async function upsertProductFieldRelevanceRule(
  productId: string,
  request: { category: string; fieldName: string; sourceScope?: string; isIrrelevant: boolean; reason?: string; decisionSource?: string; actor?: string }
) {
  const { data } = await api.post<{ message: string }>(`/products/${productId}/field-relevance-rules`, request)
  return data
}

export async function autoSuggestProductFieldRelevance(productId: string) {
  const { data } = await api.post<{ message: string; created: number }>(`/products/${productId}/field-relevance-rules/auto-suggest`)
  return data
}

export interface ProductFieldKeyRuleDto {
  id: string
  productFamily: string
  category: string
  fieldName: string
  sourceScope: string
  isKeyField: boolean
  compareMode: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion: boolean
  decisionSource: string
  reason?: string
  updatedUtc: string
}

export async function fetchProductFieldKeyRules(productId: string) {
  const { data } = await api.get<ProductFieldKeyRuleDto[]>(`/products/${productId}/field-key-rules`)
  return data
}

export async function upsertProductFieldKeyRule(
  productId: string,
  request: {
    category: string
    fieldName: string
    sourceScope?: string
    isKeyField: boolean
    compareMode?: 'Exact' | 'Pattern' | 'Semantic'
    useAiSecondOpinion?: boolean
    reason?: string
    decisionSource?: string
    actor?: string
  }
) {
  const { data } = await api.post<{ message: string }>(`/products/${productId}/field-key-rules`, request)
  return data
}

export async function fetchProductFieldMatchRules(productId: string) {
  const { data } = await api.get<ProductFieldMatchRuleDto[]>(`/products/${productId}/field-match-rules`)
  return data
}

export async function upsertProductFieldMatchRule(
  productId: string,
  request: { category: string; kingspanFieldName: string; distributorFieldName: string; actor?: string }
) {
  const { data } = await api.post<{ message: string }>(`/products/${productId}/field-match-rules`, request)
  return data
}

export async function deleteProductFieldMatchRule(productId: string, ruleId: string) {
  const { data } = await api.delete<{ message: string }>(`/products/${productId}/field-match-rules/${ruleId}`)
  return data
}

export async function runProductPromptLab(
  productId: string,
  request: { targetId: string; model?: string; customPrompt?: string; fields?: string[] }
) {
  const { data } = await api.post<ProductPromptLabResponseDto>(`/products/${productId}/prompt-lab`, request)
  return data
}

export interface ProductScrapeStatusDto {
  hasRun: boolean
  runId?: string
  startedUtc?: string
  step?: string
  message?: string
  status?: string
  completedUtc?: string
  errorMessage?: string
  /** Extracted items from current run (while saving). Shown so user sees captured data. */
  previewItems?: ProductDataItemDto[]
}

export async function fetchProductScrapeStatus(productId: string) {
  const { data } = await api.get<ProductScrapeStatusDto>(`/products/${productId}/scrape-status`)
  return data
}

export interface ProductOrgLinksDto {
  productId: string
  divisionIds: string[]
  subdivisionIds: string[]
  businessEntityIds: string[]
}

export interface ProductOrgLinksSetRequest {
  divisionIds?: string[]
  subdivisionIds?: string[]
  businessEntityIds?: string[]
}

export async function fetchProductOrgLinks(productId: string) {
  const { data } = await api.get<ProductOrgLinksDto>(`/products/${productId}/org-links`)
  return data
}

export async function setProductOrgLinks(productId: string, request: ProductOrgLinksSetRequest) {
  const { data } = await api.put<{ message: string }>(`/products/${productId}/org-links`, request)
  return data
}

export async function enqueueProductComparison(targetId: string, productId: string, rulePackId: string) {
  const { data } = await api.post<{ message: string }>(`/targets/${targetId}/compare`, {
    productId,
    rulePackId,
  })
  return data
}

export async function enqueueDistributorScrape(targetId: string) {
  const { data } = await api.post<{ message?: string }>(`/targets/${targetId}/distributor-scrape`)
  return data
}

export interface ScrapeAndCompareRequest {
  productId: string
  rulePackId: string
  reason?: string
}

export interface ScrapeAndCompareResponse {
  checkRunId: string
  scrapeJobId: string
  comparisonJobId: string
  message: string
}

export async function scrapeAndCompareTarget(targetId: string, request: ScrapeAndCompareRequest) {
  const { data } = await api.post<ScrapeAndCompareResponse>(`/targets/${targetId}/scrape-and-compare`, request)
  return data
}

import axios from 'axios'

import { getApiBaseUrl } from './apiBaseUrl'

const baseURL: string = getApiBaseUrl()

export const api = axios.create({
  baseURL,
})

const AUTH_TOKEN_KEY = 'mimgrc_token'
function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY)
}

api.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// When the server returns 401 the token is expired or revoked.
// Clear stored credentials and force a redirect to the login page so the
// user is not left staring at a broken UI.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Avoid logout redirect during SSO bootstrap: Layout may call the API before
    // MsalAuthBridge has stored the bearer token (request went out without Authorization).
    if (error?.response?.status === 401) {
      const hadBearer =
        typeof error?.config?.headers?.Authorization === 'string' &&
        error.config.headers.Authorization.startsWith('Bearer ')
      if (hadBearer) {
        localStorage.removeItem('mimgrc_token')
        localStorage.removeItem('mimgrc_user')
        const loginUrl = '/login'
        if (!window.location.pathname.startsWith(loginUrl)) {
          window.location.replace(loginUrl)
        }
      }
    }
    return Promise.reject(error)
  }
)

export interface DashboardMetricsDto {
  activeCheckRuns: number
  openFindings: number
  assignedActions: number
  complianceRate: number
  overdueActions: number
}

export interface CheckRunListDto {
  id: string
  status: string
  startedUtc: string
  completedUtc?: string
  targetCount: number
  processedCount: number
  findingCount: number
  reason?: string
  errorMessage?: string
  runType?: string
  distributorId?: string
  rootUrl?: string
  rulePackId?: string
  rulePackVersion?: string
  productName?: string
  productCode?: string
}

export interface CheckRunDetailDto extends CheckRunListDto {
  findings: FindingListDto[]
  summaryJson?: string
}

export interface FindingListDto {
  id: string
  targetId: string
  checkRunId: string
  findingType: string
  severity: string
  description: string
  expectedValue?: string
  observedValue?: string
  mimClauseMappings?: string
  confidence?: number
  context?: string
  languageCode?: string
  localeCode?: string
  status: string
  targetUrl?: string
  hasScreenshotEvidence?: boolean
  hasExtractedTextEvidence?: boolean
  hasLinksEvidence?: boolean
  evidenceReadiness?: 'Ready' | 'Partial' | 'Missing' | string
  createdUtc: string
  productName?: string
  productCode?: string
  productId?: string
  productVariantId?: string
  variantCode?: string
  ruleKey?: string
  ruleType?: string
  fieldKey?: string
  matchMode?: string
  evidenceQuote?: string
}

export function getEvidenceUrls(checkRunId: string, targetId: string) {
  return {
    screenshot: `${baseURL}/frontend/evidence/${checkRunId}/${targetId}/screenshot`,
    text: `${baseURL}/frontend/evidence/${checkRunId}/${targetId}/text`,
    links: `${baseURL}/frontend/evidence/${checkRunId}/${targetId}/links`,
  }
}

export async function updateFindingStatus(id: string, newStatus: string) {
  const { data } = await api.patch(`/frontend/findings/${id}/status`, { newStatus })
  return data
}

export async function clearFindingsAndActions(request: {
  checkRunId?: string
  includeClosed?: boolean
  clearEvidenceArtifacts?: boolean
}) {
  const { data } = await api.post('/frontend/maintenance/clear-findings-actions', {
    checkRunId: request.checkRunId ?? null,
    includeClosed: request.includeClosed ?? true,
    clearEvidenceArtifacts: request.clearEvidenceArtifacts ?? false,
  })
  return data
}

/** Must match server constant in `FrontendQueryController.FullDataReset`. */
export const FULL_APPLICATION_DATA_CONFIRM_PHRASE = 'CLEAR ALL APPLICATION DATA'

export async function postFullDataReset(payload: {
  scope: 'runtime' | 'full'
  clearHangfire: boolean
  confirmationPhrase?: string
}) {
  const { data } = await api.post<{
    message: string
    scope: string
    clearedHangfire: boolean
    defaultPimCategoriesReseeded?: boolean
  }>('/frontend/maintenance/full-data-reset', {
    scope: payload.scope,
    clearHangfire: payload.clearHangfire,
    confirmationPhrase: payload.confirmationPhrase ?? null,
  })
  return data
}

export async function updateActionStatus(id: string, newStatus: string) {
  const { data } = await api.patch(`/frontend/actions/${id}/status`, { newStatus })
  return data
}

export interface ActionListDto {
  id: string
  findingId: string
  assignedTo: string
  status: string
  priority?: string
  dueDate?: string
  description?: string
}

export async function fetchMetrics() {
  const { data } = await api.get<DashboardMetricsDto>('/frontend/metrics')
  return data
}

export async function fetchCheckRuns(options?: { status?: string; take?: number }) {
  const { data } = await api.get<CheckRunListDto[]>('/frontend/check-runs', {
    params: {
      status: options?.status,
      take: options?.take,
    },
  })
  return data
}

export interface HangfireQueueDto {
  name: string
  length: number
  fetched: number
}

export interface HangfireServerDto {
  name: string
  workersCount: number
  queues: string[]
}

export interface HangfireActiveJobDto {
  jobId: string
  state: string
  handlerType: string
  method: string
  queue: string
  serverId?: string
  startedAtUtc?: string
  enqueueAtUtc?: string
  scheduledAtUtc?: string
  reason?: string
}

export interface HangfireJobStateHistoryDto {
  state: string
  createdAtUtc?: string
  reason?: string
  summary?: string
}

export interface HangfireJobProgressLogDto {
  checkRunId: string
  timestampUtc: string
  step?: string
  level?: string
  message: string
  detailsJson?: string
}

export interface HangfireJobDetailDto {
  jobId: string
  state: string
  handlerType: string
  method: string
  queue: string
  serverId?: string
  createdAtUtc?: string
  startedAtUtc?: string
  scheduledAtUtc?: string
  enqueueAtUtc?: string
  reason?: string
  exceptionType?: string
  exceptionMessage?: string
  argumentsPreview?: string
  checkRunId?: string
  stateHistory: HangfireJobStateHistoryDto[]
  progressLogs: HangfireJobProgressLogDto[]
}

export interface HangfireOverviewDto {
  enqueued: number
  processing: number
  scheduled: number
  failed: number
  succeeded: number
  deleted: number
  recurring: number
  servers: number
  queues: number
  queueDetails: HangfireQueueDto[]
  serverDetails: HangfireServerDto[]
  activeJobs: HangfireActiveJobDto[]
}

export async function fetchHangfireOverview() {
  const { data } = await api.get<HangfireOverviewDto>('/frontend/jobs/hangfire')
  return data
}

export async function fetchHangfireJobDetail(jobId: string) {
  const { data } = await api.get<HangfireJobDetailDto>(`/frontend/jobs/hangfire/${jobId}`)
  return data
}

export interface ActiveUserSessionDto {
  sessionKey: string
  userId: string
  displayName: string
  email?: string
  authMethod?: string
  firstSeenUtc: string
  lastSeenUtc: string
  durationSeconds: number
  lastPath?: string
  remoteIpAddress?: string
}

export interface ActiveUserSessionsSnapshot {
  generatedUtc: string
  activeUserCount: number
  activeSessionCount: number
  observedSessionCount: number
  activeWindowMinutes: number
  sessions: ActiveUserSessionDto[]
}

export interface ExternalApiCheckResult {
  name: string
  status: 'Healthy' | 'Unhealthy' | string
  durationMs: number
  detail?: string
  error?: string
  metadata?: unknown
}

export interface AzureOpenAiClientStatus {
  isConfigured: boolean
  provider: string
  endpoint?: string
  deploymentName?: string
  apiVersion: string
  configurationError?: string
}

export interface DetailedHealthResponse {
  status: 'Healthy' | 'Unhealthy' | string
  generatedUtc: string
  checks: ExternalApiCheckResult[]
  aiProvider: AzureOpenAiClientStatus
}

export interface SmokeTestResponse {
  status: 'Passed' | 'Failed' | string
  triggeredUtc: string
  checks: ExternalApiCheckResult[]
}

export async function fetchActiveUserSessions(activeWindowMinutes = 15) {
  const { data } = await api.get<ActiveUserSessionsSnapshot>('/frontend/observability/sessions', {
    params: { activeWindowMinutes },
  })
  return data
}

export async function fetchDetailedHealth() {
  const { data } = await api.get<DetailedHealthResponse>('/health/detailed')
  return data
}

export async function runSmokeTests() {
  const { data } = await api.post<SmokeTestResponse>('/health/smoke-tests')
  return data
}

export async function sendTestEmail(toEmail?: string) {
  const { data } = await api.post<{ status: string; to?: string; durationMs?: number; error?: string }>('/health/test-email', {
    toEmail: toEmail || null,
  })
  return data
}

export async function runAiTest() {
  const { data } = await api.post<ExternalApiCheckResult>('/health/test-ai')
  return data
}

export async function fetchCheckRun(id: string) {
  const { data } = await api.get<CheckRunDetailDto>(`/frontend/check-runs/${id}`)
  return data
}

export async function cancelCheckRun(id: string) {
  const { data } = await api.post(`/frontend/check-runs/${id}/cancel`)
  return data
}

export interface ReviewContextPayload {
  market?: string
  language?: string
  reviewMode?: string
  notes?: string
}

export interface CreateProductComparisonJobsRequest {
  productId: string
  rulePackId: string
  targetIds: string[]
  mode: 'OneOff' | 'Scheduled'
  cronExpression?: string
  scheduleName?: string
  reason?: string
  initiatedBy?: string
  reviewContext?: ReviewContextPayload
}

export interface CreateProductComparisonJobsResponse {
  mode: string
  checkRunId?: string
  recurringJobId?: string
  targetCount: number
  message: string
}

export async function createProductComparisonJobs(request: CreateProductComparisonJobsRequest) {
  const { data } = await api.post<CreateProductComparisonJobsResponse>('/checkruns/product-comparison-jobs', request)
  return data
}

export interface CreateDistributorComparisonJobsRequest {
  distributorId: string
  rulePackId: string
  mode?: 'OneOff' | 'Scheduled' | string
  reason?: string
  initiatedBy?: string
  reviewContext?: ReviewContextPayload
}

export interface DistributorComparisonCreatedRunDto {
  productId: string
  checkRunId: string
  targetCount: number
}

export interface DistributorComparisonSkippedProductDto {
  productId: string
  reason: string
}

export interface CreateDistributorComparisonJobsResponse {
  mode: string
  distributorId: string
  productCount: number
  runCount: number
  targetCount: number
  createdRuns: DistributorComparisonCreatedRunDto[]
  skippedProducts: DistributorComparisonSkippedProductDto[]
  message: string
}

export async function createDistributorComparisonJobs(request: CreateDistributorComparisonJobsRequest) {
  const { data } = await api.post<CreateDistributorComparisonJobsResponse>('/checkruns/distributor-comparison-jobs', request)
  return data
}

export function getCheckRunReportPdfUrl(checkRunId: string) {
  return `${baseURL}/frontend/check-runs/${checkRunId}/report.pdf`
}

export function getCheckRunReportDocxUrl(checkRunId: string) {
  return `${baseURL}/frontend/check-runs/${checkRunId}/report.docx`
}

export async function downloadCheckRunReportDocx(checkRunId: string): Promise<Blob> {
  const response = await api.get(`/frontend/check-runs/${checkRunId}/report.docx`, {
    responseType: 'blob',
  })
  return response.data as Blob
}

export async function emailCheckRunReport(checkRunId: string, toEmail: string) {
  const { data } = await api.post<{ message: string; to: string }>(`/frontend/check-runs/${checkRunId}/email-report`, {
    toEmail,
  })
  return data
}

export async function downloadCheckRunReportPdf(checkRunId: string): Promise<Blob> {
  const response = await api.get(`/frontend/check-runs/${checkRunId}/report.pdf`, {
    responseType: 'blob',
    validateStatus: () => true,
  })
  if (response.status >= 200 && response.status < 300) {
    return response.data as Blob
  }
  let message = `PDF export failed (HTTP ${response.status}). `
  if (response.data instanceof Blob) {
    try {
      const text = await (response.data as Blob).text()
      const parsed = JSON.parse(text)
      if (parsed.message) message += parsed.message
      else if (parsed.detail) message += parsed.detail
    } catch {
      if ((response.data as Blob).type?.includes('json')) {
        message += 'Report may not be available for this run.'
      }
    }
  }
  throw new Error(message)
}

export async function fetchFindings(options?: {
  severity?: string
  status?: string
  checkRunId?: string
  fromUtc?: string
  toUtc?: string
  productId?: string
  variantId?: string
  page?: number
  pageSize?: number
}) {
  const { data } = await api.get<FindingsPageDto | FindingListDto[]>('/frontend/findings', {
    params: {
      severity: options?.severity,
      status: options?.status,
      checkRunId: options?.checkRunId,
      fromUtc: options?.fromUtc,
      toUtc: options?.toUtc,
      productId: options?.productId,
      variantId: options?.variantId,
      page: options?.page ?? 1,
      pageSize: options?.pageSize ?? 100,
    },
  })
  // Support both the new paged shape and a legacy bare array during rollout.
  if (Array.isArray(data)) {
    return {
      items: data,
      totalCount: data.length,
      page: 1,
      pageSize: data.length,
      totalPages: 1,
    } satisfies FindingsPageDto
  }
  return data
}

export interface FindingsPageDto {
  items: FindingListDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export async function fetchActions(options?: { status?: string; checkRunId?: string }) {
  const { data } = await api.get<ActionListDto[]>('/frontend/actions', {
    params: {
      status: options?.status,
      checkRunId: options?.checkRunId,
    },
  })
  return data
}

export interface AuditLogEntryDto {
  id: string
  actionUtc: string
  actor: string
  actionType: string
  entityType: string
  entityId: string
  description?: string
  result: string
}

export interface ComplianceSummaryReportDto {
  fromUtc: string
  toUtc: string
  sitesChecked: number
  productPagesDiscovered: number
  checksPerformed: number
  findingsDetected: number
  actionsCreated: number
}

export async function fetchAuditLog(params: {
  entityType?: string
  entityId?: string
  fromUtc?: string
  toUtc?: string
  take?: number
}) {
  const { data } = await api.get<AuditLogEntryDto[]>('/frontend/audit-log', {
    params,
  })
  return data
}

export async function fetchComplianceSummary(fromUtc?: string, toUtc?: string) {
  const { data } = await api.get<ComplianceSummaryReportDto>('/frontend/reports/compliance-summary', {
    params: { fromUtc, toUtc },
  })
  return data
}

export interface TermReferenceRowDto {
  canonicalAttributeId: string
  productFamily: string
  category: string
  canonicalTerm: string
  isCoreTerm: boolean
  compareMode: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion: boolean
  decisionSource?: string
  decisionReason?: string
  scope: {
    isGlobal: boolean
    divisionId?: string
    subdivisionId?: string
    businessEntityId?: string
    scopeLinkCount: number
  }
  alternatives: string[]
  valueShape?: string
  extractionScope?: string
  variantScoped?: boolean
  extractionHintsJson?: string
  marketingMaxLength?: number
  canonicalUnit?: string
}

export interface UpsertTermReferenceRequest {
  productFamily?: string
  category?: string
  canonicalTerm: string
  alternatives?: string[]
  isCoreTerm?: boolean
  compareMode?: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion?: boolean
  applyScope?: boolean
  scope?: {
    divisionId?: string
    subdivisionId?: string
    businessEntityId?: string
  }
  actor?: string
}

export interface UpdateTermReferenceRequest {
  canonicalTerm?: string
  alternatives?: string[]
  isCoreTerm?: boolean
  compareMode?: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion?: boolean
  applyScope?: boolean
  scope?: {
    divisionId?: string
    subdivisionId?: string
    businessEntityId?: string
  }
  actor?: string
  valueShape?: string
  extractionScope?: string
  variantScoped?: boolean
  extractionHintsJson?: string
  marketingMaxLength?: number
  canonicalUnit?: string
}

export interface AiFirstPassTermSuggestionDto {
  canonicalAttributeId: string
  canonicalTerm: string
  compareMode: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion: boolean
  source: string
  reason: string
}

export interface AiFirstPassTermReferenceResponse {
  message: string
  appliedCount: number
  suggestions: AiFirstPassTermSuggestionDto[]
}

export interface PimMasterCategoryDto {
  id: string
  code: string
  displayName: string
  sortOrder: number
  isActive: boolean
}

export interface ProductFamilyPimCategoriesAssignmentDto {
  productFamily: string
  usesExplicitFamilySubset: boolean
  assignedMasterCategoryIds: string[]
  effectiveCategoryCodes: string[]
}

export interface ProductPimCategoriesEffectiveDto {
  productId: string
  productFamily?: string | null
  familyCategories: PimMasterCategoryDto[]
  effectiveCategories: PimMasterCategoryDto[]
  excludedFromFamily: PimMasterCategoryDto[]
  includedExtras: PimMasterCategoryDto[]
}

export async function fetchPimMasterCategories(includeInactive = false) {
  const { data } = await api.get<PimMasterCategoryDto[]>('/pim-category-catalog/master', {
    params: { includeInactive },
  })
  return data
}

export async function createPimMasterCategory(body: { code: string; displayName: string; sortOrder: number }) {
  const { data } = await api.post<PimMasterCategoryDto>('/pim-category-catalog/master', body)
  return data
}

export async function updatePimMasterCategory(
  id: string,
  body: { displayName?: string; sortOrder?: number; isActive?: boolean },
) {
  const { data } = await api.put<PimMasterCategoryDto>(`/pim-category-catalog/master/${id}`, body)
  return data
}

export async function fetchFamilyPimCategoryAssignments(productFamily: string) {
  const { data } = await api.get<ProductFamilyPimCategoriesAssignmentDto>('/pim-category-catalog/family', {
    params: { productFamily },
  })
  return data
}

export async function saveFamilyPimCategoryAssignments(productFamily: string, masterCategoryIds: string[]) {
  const { data } = await api.put<ProductFamilyPimCategoriesAssignmentDto>(
    '/pim-category-catalog/family',
    { masterCategoryIds },
    { params: { productFamily } },
  )
  return data
}

export async function fetchProductPimCategoriesEffective(productId: string) {
  const { data } = await api.get<ProductPimCategoriesEffectiveDto>(`/pim-category-catalog/product/${productId}/effective`)
  return data
}

export async function saveProductPimCategoryAdjustments(
  productId: string,
  body: { excludeMasterCategoryIds: string[]; includeExtraMasterCategoryIds: string[] },
) {
  const { data } = await api.put<ProductPimCategoriesEffectiveDto>(
    `/pim-category-catalog/product/${productId}/adjustments`,
    body,
  )
  return data
}

export async function fetchTermReferenceTable(params?: {
  productFamily?: string
  category?: string
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
}) {
  const { data } = await api.get<TermReferenceRowDto[]>('/settings/term-reference', { params })
  return data
}

export async function upsertTermReference(request: UpsertTermReferenceRequest) {
  const { data } = await api.post<{ message: string }>('/settings/term-reference', request)
  return data
}

export async function updateTermReference(canonicalAttributeId: string, request: UpdateTermReferenceRequest) {
  const { data } = await api.put<{ message: string }>(`/settings/term-reference/${canonicalAttributeId}`, request)
  return data
}

export async function deleteTermReference(canonicalAttributeId: string) {
  const { data } = await api.delete<{ message: string }>(`/settings/term-reference/${canonicalAttributeId}`)
  return data
}

export async function renameProductFamily(request: { currentName: string; newName: string; actor?: string }) {
  const { data } = await api.put<{ message: string; updated: number }>('/settings/product-families/rename', request)
  return data
}

export async function deleteProductFamily(name: string) {
  const { data } = await api.delete<{ message: string; removedAttributes: number }>('/settings/product-families', {
    params: { name },
  })
  return data
}

export async function runAiFirstPassTermReference(request: {
  productFamily?: string
  category?: string
  canonicalAttributeIds?: string[]
  apply?: boolean
  actor?: string
}) {
  const { data } = await api.post<AiFirstPassTermReferenceResponse>('/settings/term-reference/ai-first-pass', request)
  return data
}

export async function importMasterTermToTermReference(request: {
  masterTermId: string
  productFamily: string
  category?: string
  includeTranslationsAsAliases?: boolean
  additionalAliases?: string[]
  isCoreTerm?: boolean
  compareMode?: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion?: boolean
  applyScope?: boolean
  scope?: {
    divisionId?: string
    subdivisionId?: string
    businessEntityId?: string
  }
  actor?: string
}) {
  const { data } = await api.post<{ message: string; canonicalAttributeId: string }>('/settings/term-reference/import-master-term', request)
  return data
}

export interface MasterTermAliasDto {
  id: string
  languageCode?: string
  alias: string
}

export interface MasterTermTranslationDto {
  id: string
  languageCode: string
  localizedTerm: string
  isPreferred: boolean
}

export interface ProductFamilyMasterTermDto {
  id: string
  productFamily: string
  status: string
  isFamilyExtension: boolean
  notes?: string
}

export interface MasterTermDto {
  id: string
  canonicalTerm: string
  normalizedTerm: string
  category: string
  dataType: string
  status: string
  description?: string
  proposedBy?: string
  approvedBy?: string
  approvedUtc?: string
  createdUtc: string
  updatedUtc?: string
  aliases: MasterTermAliasDto[]
  translations: MasterTermTranslationDto[]
  productFamilies: ProductFamilyMasterTermDto[]
}

export interface MasterTermAliasInputDto {
  languageCode?: string
  alias: string
}

export interface MasterTermTranslationInputDto {
  languageCode: string
  localizedTerm: string
  isPreferred?: boolean
}

export interface ProductFamilyMasterTermInputDto {
  productFamily: string
  status?: string
  isFamilyExtension?: boolean
  notes?: string
}

export interface UpsertMasterTermRequest {
  canonicalTerm?: string
  category?: string
  dataType?: string
  status?: string
  description?: string
  aliases?: MasterTermAliasInputDto[]
  translations?: MasterTermTranslationInputDto[]
  productFamilies?: ProductFamilyMasterTermInputDto[]
}

export interface BulkMasterTermImportRequest {
  terms: UpsertMasterTermRequest[]
}

export interface PromoteFamilyExtensionRequest {
  productFamily: string
  notes?: string
}

export interface MasterTermHistoryDto {
  id: string
  actionUtc: string
  actor: string
  actionType: string
  description?: string
  oldValuesJson?: string
  newValuesJson?: string
}

export async function fetchMasterTerms(params?: {
  search?: string
  category?: string
  status?: string
  languageCode?: string
  productFamily?: string
}) {
  const { data } = await api.get<MasterTermDto[]>('/master-terms', { params: params ?? {} })
  return data
}

export async function createMasterTerm(request: UpsertMasterTermRequest) {
  const { data } = await api.post<{ id: string; message: string }>('/master-terms', request)
  return data
}

export async function updateMasterTerm(id: string, request: UpsertMasterTermRequest) {
  const { data } = await api.put<{ message: string }>(`/master-terms/${id}`, request)
  return data
}

export async function approveMasterTerm(id: string) {
  const { data } = await api.post<{ message: string }>(`/master-terms/${id}/approve`)
  return data
}

export async function retireMasterTerm(id: string) {
  const { data } = await api.post<{ message: string }>(`/master-terms/${id}/retire`)
  return data
}

export async function deleteMasterTerm(id: string) {
  await api.delete(`/master-terms/${id}`)
}

export async function bulkImportMasterTerms(request: BulkMasterTermImportRequest) {
  const { data } = await api.post<{ created: number; skipped: number }>('/master-terms/import', request)
  return data
}

export async function promoteMasterTermFamilyExtension(id: string, request: PromoteFamilyExtensionRequest) {
  const { data } = await api.post<{ message: string }>(`/master-terms/${id}/promote-family-extension`, request)
  return data
}

export async function fetchMasterTermHistory(id: string) {
  const { data } = await api.get<MasterTermHistoryDto[]>(`/master-terms/${id}/history`)
  return data
}

export function getMasterTermsExportUrl(format: 'json' | 'csv' = 'json') {
  return `${baseURL}/master-terms/export?format=${format}`
}

// Targets
export interface TargetDto {
  id: string
  url: string
  description?: string
  contentType?: string
  healthStatus?: string
  languageCode?: string
  localeCode?: string
  distributorId: string
  productId: string
}

export interface CreateTargetRequest {
  url: string
  description?: string
}

export interface ComplianceCheckResult {
  checkRunId: string
  status: string
  url: string
  observedFacts?: {
    facts?: {
      page_title?: string
      links_found?: number
      links_valid?: number
      text_length?: number
    }
  }
  findings?: Array<{
    severity: string
    description: string
    clause: string
  }>
  actions?: Array<{
    id: string
    status: string
    priority: string
  }>
  completedUtc?: string
}

export async function fetchTargets() {
  const { data } = await api.get<TargetDto[]>('/frontend/targets')
  return data
}

export async function createTarget(request: CreateTargetRequest) {
  const { data } = await api.post<TargetDto>('/frontend/targets', request)
  return data
}

export async function runComplianceCheck(url: string) {
  const { data } = await api.post<ComplianceCheckResult>('/debug/compliance-check', { url })
  return data
}

// Distributors (admin)
export interface DistributorDto {
  id: string
  name: string
  domain: string
  websiteUrl?: string
  websiteCountry?: string
  market?: string
  approvalStatus?: string
  organizationType: 'Unclassified' | 'AccreditedDistributor' | 'IndependentReseller'
  companyNumber?: string
  marketingEmail?: string
  address?: string
  contactName?: string
  contactEmail?: string
  primaryContactEmail?: string
  secondaryContactEmail?: string
  phone?: string
  notes?: string
  businessEntityId?: string
  businessEntityName?: string
   divisionId?: string
  ownerAppUserId?: string
  ownerDisplayName?: string
  ownerEmail?: string
}

export interface DistributorAiEnrichResult {
  name?: string
  address?: string
  phone?: string
  companyNumber?: string
  marketingEmail?: string
  primaryContactEmail?: string
  secondaryContactEmail?: string
  websiteCountry?: string
  notes?: string
  source?: string
  message?: string
}

export interface DistributorDiscoverRequest {
  rootUrl?: string
  maxDiscoveredTargets?: number
}

export interface CreateDistributorRequest {
  rootUrl: string
  companyName?: string
  name?: string
  businessEntityId?: string
   divisionId?: string
  market?: string
  approvalStatus?: string
  organizationType?: 'Unclassified' | 'AccreditedDistributor' | 'IndependentReseller'
  companyNumber?: string
  marketingEmail?: string
  address?: string
  phone?: string
  contactName?: string
  contactEmail?: string
  primaryContactEmail?: string
  secondaryContactEmail?: string
  notes?: string
  websiteUrl?: string
  websiteCountry?: string
  ownerAppUserId?: string
}

export interface UpdateDistributorRequest {
  name?: string
  businessEntityId?: string
  businessEntityIdCleared?: boolean
   divisionId?: string
   divisionIdCleared?: boolean
  market?: string
  approvalStatus?: string
  organizationType?: 'Unclassified' | 'AccreditedDistributor' | 'IndependentReseller'
  companyNumber?: string
  marketingEmail?: string
  address?: string
  phone?: string
  contactName?: string
  contactEmail?: string
  primaryContactEmail?: string
  secondaryContactEmail?: string
  notes?: string
  websiteUrl?: string
  websiteCountry?: string
  ownerAppUserId?: string
  ownerAppUserIdCleared?: boolean
}

export interface AddDistributorContactAuditRequest {
  contactedUtc?: string
  reason: string
  contactMethod?: string
  notes?: string
}

export interface UpsertDistributorProductAuditRequest {
  lastAuditUtc?: string
  nextAuditDueUtc?: string
  notes?: string
}

export interface DistributorContactAuditDto {
  id: string
  distributorId: string
  contactedUtc: string
  reason: string
  contactMethod?: string
  notes?: string
  createdUtc: string
  createdBy?: string
}

export interface DistributorCheckedUrlDto {
  targetId: string
  url: string
  productId: string
  productCode: string
  productName: string
  lastCheckedUtc?: string
  healthStatus?: string
}

export interface DistributorProductAuditDto {
  id: string
  distributorId: string
  productId: string
  productCode: string
  productName: string
  lastAuditUtc?: string
  nextAuditDueUtc?: string
  notes?: string
}

export interface DistributorProductReportDto {
  productId: string
  productCode: string
  productName: string
  urlCount: number
  lastCheckedUtc?: string
  lastAuditUtc?: string
  nextAuditDueUtc?: string
  notes?: string
  checkedUrls: string[]
}

export interface DistributorReportDto {
  distributorId: string
  distributorName: string
  generatedUtc: string
  contactAudits: DistributorContactAuditDto[]
  checkedUrls: DistributorCheckedUrlDto[]
  productsFound: DistributorProductReportDto[]
}

export async function fetchDistributors() {
  const { data } = await api.get<DistributorDto[]>('/distributors')
  return data
}

export async function createDistributor(request: CreateDistributorRequest) {
  const { data } = await api.post<DistributorDto>('/distributors', request)
  return data
}

export async function updateDistributor(id: string, request: UpdateDistributorRequest) {
  const { data } = await api.put<DistributorDto>(`/distributors/${id}`, request)
  return data
}

export async function enrichDistributorWithAi(id: string) {
  const { data } = await api.post<DistributorAiEnrichResult>(`/distributors/${id}/ai-enrich`)
  return data
}

export async function discoverDistributorTargets(id: string, request: DistributorDiscoverRequest) {
  const { data } = await api.post<{ jobId: string }>(`/distributors/${id}/discover-targets`, request)
  return data
}

export async function addDistributorContactAudit(id: string, request: AddDistributorContactAuditRequest) {
  const { data } = await api.post<DistributorContactAuditDto>(`/distributors/${id}/contact-audits`, request)
  return data
}

export async function upsertDistributorProductAudit(id: string, productId: string, request: UpsertDistributorProductAuditRequest) {
  const { data } = await api.put<DistributorProductAuditDto>(`/distributors/${id}/product-audits/${productId}`, request)
  return data
}

export async function fetchDistributorReport(id: string) {
  const { data } = await api.get<DistributorReportDto>(`/distributors/${id}/report`)
  return data
}

// Rule Packs & Compliance Rules
export interface RulePackDto {
  id: string
  name: string
  version: string
  status: 'Draft' | 'Approved' | 'Retired'
  description?: string
  owner?: string
  createdUtc?: string
  updatedUtc?: string
  languageCode?: string
  baseRulePackId?: string | null
}

export interface ComplianceRuleDto {
  id: string
  rulePackId: string
  key: string
  name: string
  type: 'RestrictedTerm' | 'UnsupportedClaim' | 'RequiredClaim' | 'BrokenLink' | 'FieldRule'
  pattern?: string
  parametersJson?: string
  severity: 'Critical' | 'High' | 'Medium' | 'Low' | 'Info'
  clauses?: string
  enabled: boolean
  createdUtc?: string
  updatedUtc?: string
}


export async function fetchRulePacks() {
  const { data } = await api.get<RulePackDto[]>('/rulepacks')
  return data
}


export async function createRulePack(request: { name: string; version: string; description?: string; owner?: string }) {
  const { data } = await api.post<RulePackDto>('/rulepacks', request)
  return data
}


export async function fetchRulePackWithRules(id: string) {
  const { data } = await api.get<{ pack: RulePackDto; rules: ComplianceRuleDto[] }>(`/rulepacks/${id}`)
  return data
}

export async function fetchRulePackTranslations(id: string) {
  const { data } = await api.get<{
    rootId: string
    languages: string[]
    translations: Array<{
      id: string
      name: string
      version: string
      status: string
      languageCode: string
      baseRulePackId?: string | null
      createdUtc?: string
      updatedUtc?: string
    }>
  }>(`/rulepacks/${id}/translations`)
  return data
}

export async function translateRulePack(id: string, languages?: string[]) {
  const { data } = await api.post<{
    message: string
    packs: Array<{ id: string; name: string; version: string; status: string; languageCode: string; baseRulePackId?: string | null }>
  }>(`/rulepacks/${id}/translations`, { languages: languages ?? [] })
  return data
}

export async function downloadRulePackExport(id: string, format: 'json' | 'csv' = 'json'): Promise<Blob> {
  const response = await api.get(`/rulepacks/${id}/export`, {
    params: { format },
    responseType: 'blob',
  })
  return response.data as Blob
}


export async function updateRulePackStatus(id: string, status: 'Draft' | 'Approved' | 'Retired') {
  await api.patch(`/rulepacks/${id}/status`, { status })
}

export async function cloneRulePack(id: string, version: string) {
  const { data } = await api.post<RulePackDto>(`/rulepacks/${id}/clone`, { version })
  return data
}

export interface ExtractionProfileDto {
  id: string
  name: string
  version: string
  status: 'Draft' | 'Approved' | 'Retired'
  approvedBy?: string
  approvedUtc?: string
  createdUtc?: string
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
  divisionName?: string
  businessEntityName?: string
  fieldCount: number
}

// ValueType is a plain (non-string) enum on the API with no JSON string converter configured, so
// it serializes as its ordinal (Text=0 ... Json=5). This label array's order must match
// MimGrc.Shared.Models.ExtractionValueType exactly - use the helpers below to convert.
export const extractionValueTypeLabels = ['Text', 'Number', 'Boolean', 'Date', 'Url', 'Json'] as const
export type ExtractionValueTypeLabel = (typeof extractionValueTypeLabels)[number]

export function extractionValueTypeToLabel(value: number): ExtractionValueTypeLabel {
  return extractionValueTypeLabels[value] ?? 'Text'
}

export function extractionValueTypeToOrdinal(label: ExtractionValueTypeLabel): number {
  return extractionValueTypeLabels.indexOf(label)
}

export interface ExtractionFieldDefinitionDto {
  id: string
  extractionProfileRevisionId: string
  sourceOfTruthFieldId?: string
  canonicalFieldKey: string
  displayName?: string
  aliasesJson?: string
  sectionHintsJson?: string
  valueType: number
  unit?: string
  validationRulesJson?: string
  requiredOnWebsite: boolean
  claimCheckRole?: string
  sortOrder: number
}

export async function fetchExtractionProfiles() {
  const { data } = await api.get<ExtractionProfileDto[]>('/extraction-profiles')
  return data
}

export async function fetchExtractionProfileById(id: string) {
  const { data } = await api.get<{
    id: string
    name: string
    version: string
    status: 'Draft' | 'Approved' | 'Retired'
    divisionId?: string
    subdivisionId?: string
    businessEntityId?: string
    fields: ExtractionFieldDefinitionDto[]
  }>(`/extraction-profiles/${id}`)
  return data
}

export async function createExtractionProfile(request: {
  name: string
  version: string
  fields?: Array<{
    canonicalFieldKey: string
    displayName?: string
    valueType?: ExtractionValueTypeLabel
    unit?: string
    requiredOnWebsite?: boolean
    claimCheckRole?: string
  }>
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
}) {
  const { data } = await api.post<ExtractionProfileDto>('/extraction-profiles', {
    ...request,
    fields: request.fields?.map((field) => ({
      ...field,
      valueType: field.valueType ? extractionValueTypeToOrdinal(field.valueType) : undefined,
    })),
  })
  return data
}

export interface PcrFieldCoverageDto {
  canonicalFieldKey: string
  displayName?: string
  suggestedValueType: ExtractionValueTypeLabel
  suggestedUnit?: string
  suggestedRequiredOnWebsite: boolean
  suggestedClaimCheckRole?: string
  recordsWithData: number
  totalRecords: number
}

export interface PcrFieldCoverageResponse {
  totalApprovedPcrCount: number
  fields: PcrFieldCoverageDto[]
}

export async function fetchPcrFieldCoverage(params?: {
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
  productId?: string
}) {
  const { data } = await api.get<PcrFieldCoverageResponse>('/extraction-profiles/pcr-field-coverage', { params })
  return data
}

export async function updateExtractionProfileStatus(id: string, status: 'Draft' | 'Approved' | 'Retired') {
  await api.patch(`/extraction-profiles/${id}/status`, { status })
}

export interface EnsuredExtractionProfileDto {
  id: string
  name: string
  version: string
  status: string
  alreadyExisted: boolean
  matchedScope?: string
  fieldCount?: number
}

export async function ensureExtractionProfileForProduct(productId: string) {
  const { data } = await api.post<EnsuredExtractionProfileDto>('/extraction-profiles/ensure-for-product', {
    productId,
  })
  return data
}

export async function updateExtractionProfileScope(id: string, scope: {
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
  clearScope?: boolean
}) {
  await api.patch(`/extraction-profiles/${id}/scope`, scope)
}

export async function cloneExtractionProfile(id: string, version: string, scope?: {
  clearScope?: boolean
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
}) {
  const { data } = await api.post<ExtractionProfileDto>(`/extraction-profiles/${id}/clone`, { version, ...scope })
  return data
}

export async function addExtractionProfileField(profileId: string, field: {
  canonicalFieldKey: string
  displayName?: string
  aliases?: string[]
  valueType: ExtractionValueTypeLabel
  unit?: string
  requiredOnWebsite: boolean
  claimCheckRole?: string
}) {
  const { data } = await api.post<ExtractionFieldDefinitionDto>(`/extraction-profiles/${profileId}/fields`, {
    ...field,
    valueType: extractionValueTypeToOrdinal(field.valueType),
  })
  return data
}

export async function updateExtractionProfileField(profileId: string, fieldId: string, patch: {
  displayName?: string
  aliases?: string[]
  valueType?: ExtractionValueTypeLabel
  unit?: string
  requiredOnWebsite?: boolean
  claimCheckRole?: string
}) {
  await api.patch(`/extraction-profiles/${profileId}/fields/${fieldId}`, {
    ...patch,
    valueType: patch.valueType ? extractionValueTypeToOrdinal(patch.valueType) : undefined,
  })
}

export async function deleteExtractionProfileField(profileId: string, fieldId: string) {
  await api.delete(`/extraction-profiles/${profileId}/fields/${fieldId}`)
}


export async function createComplianceRule(
  rulePackId: string,
  request: { key: string; name: string; type: string; pattern?: string; parametersJson?: string; severity: string; clauses?: string; enabled?: boolean }
) {
  const { data } = await api.post<ComplianceRuleDto>(`/rulepacks/${rulePackId}/rules`, request)
  return data
}


export async function updateComplianceRule(
  id: string,
  request: { name?: string; pattern?: string; parametersJson?: string; severity?: string; clauses?: string; enabled?: boolean }
) {
  await api.put(`/rules/${id}`, request)
}


export async function deleteComplianceRule(id: string) {
  await api.delete(`/rules/${id}`)
}

// Products with approved Source of Truth (for Run Checks)
export async function fetchProductsWithApprovedSourceOfTruth() {
  const { data } = await api.get<ProductDto[]>('/frontend/products-with-approved-sot')
  return data
}

export interface OrphanedProductDto {
  productId: string
  code: string
  name: string
  market?: string
  family?: string
  isApproved: boolean
  targetCount: number
  distributorCount: number
  distributorNames: string[]
  exampleUrls: string[]
  lastCheckedUtc?: string | null
}

export async function fetchOrphanedDistributorProducts() {
  const { data } = await api.get<OrphanedProductDto[]>('/frontend/products/orphaned-distributor')
  return data
}

// Auth (dev login)
export interface PasswordLoginRequest {
  identifier: string
  password: string
}

export interface PasswordLoginResponse {
  token: string
  expiresInSeconds: number
  user: {
    userId: string
    displayName: string
    email?: string
    role?: string
    businessLevel?: string
    functionalRoles?: string[]
    divisionId?: string
    subdivisionId?: string
    businessEntityIds?: string[]
  }
}

export interface DevLoginRequest {
  userId: string
  displayName?: string
  email?: string
  role?: string
  businessLevel?: string
  functionalRoles?: string[]
  divisionId?: string
  subdivisionId?: string
  businessEntityIds?: string[]
}

export interface DevLoginResponse {
  token: string
  expiresInSeconds: number
}

export interface DevUserDto {
  id: string
  userId: string
  displayName?: string
  email?: string
  role: string
  businessLevel: string
  functionalRoles: string[]
  divisionId?: string
  subdivisionId?: string
  businessEntityIds: string[]
  createdUtc: string
}

export async function devLogin(request: DevLoginRequest) {
  const { data } = await api.post<DevLoginResponse>('/auth/dev-login', {
    ...request,
    divisionId: request.divisionId || null,
    subdivisionId: request.subdivisionId || null,
  })
  return data
}

export async function passwordLogin(request: PasswordLoginRequest) {
  const { data } = await api.post<PasswordLoginResponse>('/auth/login', request)
  return data
}

export async function fetchDevUsers() {
  const { data } = await api.get<DevUserDto[]>('/auth/dev-users')
  return data
}

export interface MyProfileDto {
  userId: string
  displayName: string
  email?: string
  role: string
  businessLevel: string
  functionalRoles: string[]
  divisionId?: string
  subdivisionId?: string
  businessEntityIds: string[]
  divisionName?: string
  subdivisionName?: string
  businessEntityNames?: string[]
  isAutoProvisioned: boolean
  isProfileComplete?: boolean
  isImpersonating?: boolean
  impersonatorUserId?: string
  impersonatorDisplayName?: string
  entraObjectId?: string
  entraTenantId?: string
  entraPreferredUsername?: string
  entraName?: string
  entraIdentityProvider?: string
}

export async function getMyProfile() {
  const { data } = await api.get<MyProfileDto>('/auth/me')
  return data
}

export async function updateMyProfile(request: {
  businessLevel: string
  divisionId?: string
  subdivisionId?: string
  businessEntityIds?: string[]
}) {
  const { data } = await api.put<MyProfileDto>('/auth/me/profile', request)
  return data
}

export async function getMyProfileWithToken(accessToken: string) {
  const { data } = await api.get<MyProfileDto>('/auth/me', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
  return data
}

// ─── PIM Product Data Import / Export ─────────────────────────────────────────

export interface ProductDataImportResult {
  totalRows: number
  created: number
  updated: number
  skipped: number
  errors: { rowNumber: number; code?: string; message: string }[]
}

/**
 * Import product attribute data from a long-format CSV file directly from PIM.
 * CSV columns: Code, Category, AttributeName, Value, Unit, DisplayLabel, DataType, LanguageCode
 * mode: 'replace' (default) – replaces all existing PIM data for matched products
 *       'merge' – upserts by product+category+attributeName
 */
export async function importProductDataFromCsv(
  file: File,
  mode: 'replace' | 'merge' = 'replace',
) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post<ProductDataImportResult>('/products/import-data', formData, {
    params: { mode },
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export interface PimCategoryFieldDto {
  productFamily: string
  category: string
  attributeName: string
  displayLabel?: string
}

export async function fetchPimCategoriesForFamily(productFamily: string) {
  const { data } = await api.get<PimCategoryFieldDto[]>('/frontend/pim-family-categories', {
    params: { productFamily },
  })
  return data
}

/**
 * Export product attribute data (PIM source by default) as long-format CSV.
 * The exported file can be re-imported with importProductDataFromCsv.
 */
export async function exportProductDataToCsv(params?: {
  search?: string
  family?: string
  code?: string
  source?: string
}) {
  const response = await api.get('/products/export-data', {
    params: params ?? {},
    responseType: 'blob',
  })
  return response.data as Blob
}

// Users (AppUser CRUD)
export interface AppUserDto {
  id: string
  userId: string
  displayName?: string
  email?: string
  isAutoProvisioned: boolean
  role: string
  businessLevel: string
  functionalRoles: string[]
  divisionId?: string
  subdivisionId?: string
  businessEntityIds: string[]
  hasPassword: boolean
  createdUtc: string
}

export interface AppUserCreateRequest {
  userId: string
  displayName?: string
  email?: string
  role?: string
  businessLevel?: string
  functionalRoles?: string[]
  divisionId?: string
  subdivisionId?: string
  businessEntityIds?: string[]
  password: string
}

export interface AppUserUpdateRequest {
  displayName?: string
  email?: string
  role?: string
  businessLevel?: string
  functionalRoles?: string[]
  divisionId?: string | null
  subdivisionId?: string
  businessEntityIds?: string[]
  password?: string
}

export async function fetchUsers() {
  const { data } = await api.get<AppUserDto[]>('/users')
  return data
}

export async function fetchUser(id: string) {
  const { data } = await api.get<AppUserDto>(`/users/${id}`)
  return data
}

export async function createUser(request: AppUserCreateRequest) {
  const { data } = await api.post<AppUserDto>('/users', request)
  return data
}

export async function updateUser(id: string, request: AppUserUpdateRequest) {
  const { data } = await api.put<AppUserDto>(`/users/${id}`, request)
  return data
}

export async function approveUser(id: string) {
  const { data } = await api.post<{ message: string; userId: string }>(`/users/${id}/approve`)
  return data
}

export async function deleteUser(id: string) {
  await api.delete(`/users/${id}`)
}

export interface ImpersonateUserResponse {
  token: string
  expiresInSeconds: number
  user: {
    userId: string
    displayName: string
    email?: string
    role: string
    businessLevel: string
    functionalRoles: string[]
    divisionId?: string
    subdivisionId?: string
    businessEntityIds: string[]
    isImpersonating: boolean
    impersonatorUserId: string
    impersonatorDisplayName: string
  }
}

export async function impersonateUser(id: string) {
  const { data } = await api.post<ImpersonateUserResponse>(`/users/${id}/impersonate`)
  return data
}

export async function updateUserPassword(id: string, password: string) {
  const { data } = await api.put<{ message: string }>(`/users/${id}/password`, { password })
  return data
}

// Organization hierarchy (for user form dropdowns)
export interface DivisionDto {
  id: string
  name: string
  code: string
  ownerUserId?: string
}

export interface SubdivisionDto {
  id: string
  divisionId: string
  divisionName?: string
  name: string
  code: string
  ownerUserId?: string
}

export interface BusinessEntityDto {
  id: string
  subdivisionId: string
  subdivisionName?: string
  divisionName?: string
  name: string
  code: string
  address?: string
  ownerUserId?: string
  marketingManagerUserId?: string
}

export async function fetchDivisions() {
  const { data } = await api.get<DivisionDto[]>('/organizationhierarchy/divisions')
  return data
}

export async function createDivision(request: { name: string; code: string; ownerUserId?: string }) {
  const { data } = await api.post<DivisionDto>('/organizationhierarchy/divisions', request)
  return data
}

export async function updateDivision(id: string, request: { name?: string; code?: string; ownerUserId?: string }) {
  const { data } = await api.put<DivisionDto>(`/organizationhierarchy/divisions/${id}`, request)
  return data
}

export async function deleteDivision(id: string) {
  await api.delete(`/organizationhierarchy/divisions/${id}`)
}

// ── Support Tickets ───────────────────────────────────────────────────────────

export type SupportTicketCategory = 'Bug' | 'FeatureRequest' | 'Question' | 'Improvement' | 'Other'
export type SupportTicketPriority = 'Low' | 'Medium' | 'High' | 'Critical'
export type SupportTicketStatus = 'Open' | 'InProgress' | 'Resolved' | 'Closed'

export interface SupportTicketCommentDto {
  id: string
  body: string
  authorUserId: string
  authorDisplayName?: string
  isAdminReply: boolean
  createdUtc: string
}

export interface SupportTicketDto {
  id: string
  ticketNumber: string
  category: SupportTicketCategory
  title: string
  description: string
  priority: SupportTicketPriority
  status: SupportTicketStatus
  submittedByUserId: string
  submittedByDisplayName?: string
  submittedByEmail?: string
  createdUtc: string
  updatedUtc?: string
  resolvedUtc?: string
  closedUtc?: string
  adminReply?: string
  repliedByDisplayName?: string
  repliedUtc?: string
  comments: SupportTicketCommentDto[]
}

export interface SupportTicketListResult {
  total: number
  page: number
  pageSize: number
  items: SupportTicketDto[]
}

export interface SubmitSupportTicketRequest {
  category: SupportTicketCategory
  title: string
  description: string
  priority?: SupportTicketPriority
}

export async function submitSupportTicket(request: SubmitSupportTicketRequest): Promise<SupportTicketDto> {
  const { data } = await api.post<SupportTicketDto>('/support-tickets', request)
  return data
}

export async function fetchSupportTickets(params?: {
  status?: SupportTicketStatus
  category?: SupportTicketCategory
  page?: number
  pageSize?: number
}): Promise<SupportTicketListResult> {
  const { data } = await api.get<SupportTicketListResult>('/support-tickets', { params })
  return data
}

export async function fetchSupportTicket(id: string): Promise<SupportTicketDto> {
  const { data } = await api.get<SupportTicketDto>(`/support-tickets/${id}`)
  return data
}

export async function addSupportTicketComment(id: string, body: string): Promise<SupportTicketCommentDto> {
  const { data } = await api.post<SupportTicketCommentDto>(`/support-tickets/${id}/comments`, { body })
  return data
}

export async function updateSupportTicket(
  id: string,
  request: { status?: SupportTicketStatus; priority?: SupportTicketPriority; adminReply?: string }
): Promise<SupportTicketDto> {
  const { data } = await api.patch<SupportTicketDto>(`/support-tickets/${id}`, request)
  return data
}

export async function fetchSubdivisions(params?: { divisionId?: string }) {
  const { data } = await api.get<SubdivisionDto[]>('/organizationhierarchy/subdivisions', { params })
  return data
}

export async function createSubdivision(request: { divisionId: string; name: string; code: string; ownerUserId?: string }) {
  const { data } = await api.post<SubdivisionDto>('/organizationhierarchy/subdivisions', request)
  return data
}

export async function updateSubdivision(id: string, request: { divisionId?: string; name?: string; code?: string; ownerUserId?: string }) {
  const { data } = await api.put<SubdivisionDto>(`/organizationhierarchy/subdivisions/${id}`, request)
  return data
}

export async function fetchBusinessEntities(params?: { subdivisionId?: string }) {
  const { data } = await api.get<BusinessEntityDto[]>('/organizationhierarchy/business-entities', { params })
  return data
}

export async function createBusinessEntity(request: {
  subdivisionId: string
  name: string
  code: string
  address?: string
  ownerUserId?: string
  marketingManagerUserId?: string
}) {
  const { data } = await api.post<BusinessEntityDto>('/organizationhierarchy/business-entities', request)
  return data
}

export async function updateBusinessEntity(id: string, request: {
  subdivisionId?: string
  name?: string
  code?: string
  address?: string
  ownerUserId?: string
  marketingManagerUserId?: string
}) {
  const { data } = await api.put<BusinessEntityDto>(`/organizationhierarchy/business-entities/${id}`, request)
  return data
}

export async function deleteBusinessEntity(id: string) {
  await api.delete(`/organizationhierarchy/business-entities/${id}`)
}

// --- Term Watch ---

export interface TermWatchDto {
  id: string
  term: string
  countryCode: string
  cronExpression: string
  scheduleLabel?: string
  isActive: boolean
  notifyEmail: string
  notes?: string
  lastScanUtc?: string
  createdUtc: string
  createdBy?: string
}

export interface TermWatchScanDto {
  id: string
  startedUtc: string
  completedUtc?: string
  status: string
  totalResults: number
  emailSent: boolean
  errorMessage?: string
}

export interface TermWatchResultDto {
  id: string
  searchEngine: string
  position: number
  title?: string
  url: string
  snippet?: string
  domain?: string
  capturedUtc: string
  distributorExists?: boolean
  distributorId?: string | null
}

export interface TermWatchBlacklistEntry {
  id: string
  domain: string
  countryCode?: string
  reason?: string
  createdUtc: string
  createdBy?: string
}

export async function fetchTermWatches() {
  const { data } = await api.get<TermWatchDto[]>('/term-watches')
  return data
}

export async function createTermWatch(payload: {
  term: string
  countryCode: string
  cronExpression: string
  scheduleLabel?: string
  notifyEmail: string
  notes?: string
}) {
  const { data } = await api.post('/term-watches', payload)
  return data
}

export async function updateTermWatch(id: string, payload: {
  term?: string
  countryCode?: string
  cronExpression?: string
  scheduleLabel?: string
  notifyEmail?: string
  notes?: string
}) {
  const { data } = await api.put(`/term-watches/${id}`, payload)
  return data
}

export async function toggleTermWatch(id: string) {
  const { data } = await api.post(`/term-watches/${id}/toggle`)
  return data
}

export async function runTermWatchNow(id: string) {
  const { data } = await api.post(`/term-watches/${id}/run-now`)
  return data
}

export async function sendTermWatchTestEmail(id: string) {
  const { data } = await api.post<{ message: string }>(`/term-watches/${id}/test-email`)
  return data
}

export async function deleteTermWatch(id: string) {
  const { data } = await api.delete(`/term-watches/${id}`)
  return data
}

export async function fetchTermWatchScans(watchId: string) {
  const { data } = await api.get<TermWatchScanDto[]>(`/term-watches/${watchId}/scans`)
  return data
}

export async function fetchTermWatchScanResults(watchId: string, scanId: string) {
  const { data } = await api.get<TermWatchResultDto[]>(`/term-watches/${watchId}/scans/${scanId}/results`)
  return data
}

export async function addTermWatchBlacklistEntry(domain: string, countryCode?: string, reason?: string) {
  const { data } = await api.post<TermWatchBlacklistEntry>('/term-watches/blacklist', {
    domain,
    countryCode,
    reason,
  })
  return data
}

// --- Brand Keywords (whitelist for distributor search) ---

export interface BrandKeywordDto {
  id: string
  keyword: string
  isActive: boolean
  createdUtc: string
  createdBy?: string
  divisionIds: string[]
}

export async function fetchBrandKeywords() {
  const { data } = await api.get<BrandKeywordDto[]>('/brand-keywords')
  return data
}

export async function createBrandKeyword(keyword: string, divisionIds: string[]) {
  const { data } = await api.post<BrandKeywordDto>('/brand-keywords', { keyword, divisionIds })
  return data
}

export async function updateBrandKeyword(id: string, payload: { divisionIds?: string[]; isActive?: boolean }) {
  const { data } = await api.put<BrandKeywordDto>(`/brand-keywords/${id}`, payload)
  return data
}

export async function deleteBrandKeyword(id: string) {
  await api.delete(`/brand-keywords/${id}`)
}

// --- Distributor Action Reports ---
export interface DistributorActionReportRunDto {
  id: string
  distributorId: string
  distributorName?: string
  distributorDomain?: string
  name: string
  severityFilter: string
  findingStatusFilter: string
  actionStatusFilter: string
  introText?: string
  outroText?: string
  createdUtc: string
  createdBy?: string
  updatedUtc?: string
  updatedBy?: string
}

export interface UpsertDistributorActionReportRunRequest {
  distributorId: string
  name: string
  severityFilter?: string
  findingStatusFilter?: string
  actionStatusFilter?: string
  introText?: string
  outroText?: string
}

export async function fetchDistributorActionReportRuns(distributorId?: string) {
  const { data } = await api.get<DistributorActionReportRunDto[]>('/distributor-action-reports/runs', {
    params: distributorId ? { distributorId } : {},
  })
  return data
}

export async function createDistributorActionReportRun(request: UpsertDistributorActionReportRunRequest) {
  const { data } = await api.post<DistributorActionReportRunDto>('/distributor-action-reports/runs', request)
  return data
}

export async function updateDistributorActionReportRun(id: string, request: UpsertDistributorActionReportRunRequest) {
  const { data } = await api.put<DistributorActionReportRunDto>(`/distributor-action-reports/runs/${id}`, request)
  return data
}

export async function deleteDistributorActionReportRun(id: string) {
  await api.delete(`/distributor-action-reports/runs/${id}`)
}

export async function logDistributorActionReportExport(payload: {
  distributorId: string
  reportRunId?: string
  format: 'CSV' | 'PDF' | string
  rowCount: number
  severityFilter?: string
  findingStatusFilter?: string
  actionStatusFilter?: string
}) {
  const { data } = await api.post<{ message: string }>('/distributor-action-reports/export-audit', payload)
  return data
}

export interface DistributorEmailTemplateDto {
  id: string
  name: string
  subject: string
  htmlBody: string
  createdUtc: string
  createdBy: string
}

export async function fetchDistributorEmailTemplates() {
  const { data } = await api.get<DistributorEmailTemplateDto[]>('/distributor-action-reports/email-templates')
  return data
}

export async function createDistributorEmailTemplate(request: {
  name: string
  subject: string
  htmlBody?: string
}) {
  const { data } = await api.post<DistributorEmailTemplateDto>('/distributor-action-reports/email-templates', request)
  return data
}

export async function sendDistributorReportEmail(request: {
  distributorId: string
  templateId: string
  toEmail?: string
  testToSelf?: boolean
  pdfBase64?: string
  pdfFileName?: string
}) {
  const { data } = await api.post<{ message: string }>('/distributor-action-reports/send-email', {
    distributorId: request.distributorId,
    templateId: request.templateId,
    toEmail: request.toEmail,
    testToSelf: !!request.testToSelf,
    pdfBase64: request.pdfBase64,
    pdfFileName: request.pdfFileName,
  })
  return data
}

// --- Distributor Firecrawl Search ---

export interface DistributorSearchResultDto {
  position: number
  url: string
  title?: string
  description?: string
}

export interface DistributorFirecrawlSearchResponse {
  results: DistributorSearchResultDto[]
  brandKeywordCount: number
  firecrawlWebHits: number
  mapHitsConsidered: number
  skippedExistingTargets: number
  message?: string
}

export interface ProductSoTReadinessIssueDto {
  code: string
  message: string
}

export interface ProductSoTReadinessDto {
  productId: string
  isReady: boolean
  hasKingspanUrl: boolean
  hasFamily: boolean
  kingspanFactCount: number
  isPlaceholder: boolean
  productCode?: string
  productName?: string
  family?: string
  kingspanUrl?: string
  issues: ProductSoTReadinessIssueDto[]
}

export interface ConfirmCreateKingspanProductRequest {
  code: string
  name: string
  kingspanUrl: string
  family: string
  country?: string
  division?: string
  market?: string
}

export interface DistributorConfirmScrapeRequest {
  urls: string[]
  kingspanProductId?: string
  createKingspan?: ConfirmCreateKingspanProductRequest
  catalogOnly?: boolean
  resolveVariants?: boolean
  urlTitles?: Record<string, string>
}

export interface DistributorConfirmScrapeResponse {
  kingspanProductId: string
  soTReady: boolean
  targetsCreated: number
  targetsPendingSoT: number
  jobsEnqueued: number
  readiness?: ProductSoTReadinessDto
  message: string
}

export async function searchDistributorWebsite(distributorId: string) {
  const { data } = await api.post<DistributorFirecrawlSearchResponse>(`/distributors/${distributorId}/firecrawl-search`)
  return data
}

export async function searchDistributorProductVariants(
  distributorId: string,
  kingspanProductId: string,
  productQuery?: string,
) {
  const { data } = await api.post<DistributorFirecrawlSearchResponse>(
    `/distributors/${distributorId}/product-variant-search`,
    undefined,
    {
      params: {
        kingspanProductId,
        ...(productQuery ? { productQuery } : {}),
      },
    },
  )
  return data
}

export async function searchDistributorCatalog(distributorId: string) {
  const { data } = await api.post<DistributorFirecrawlSearchResponse>(`/distributors/${distributorId}/catalog-search`)
  return data
}

export async function deleteDistributor(distributorId: string) {
  const { data } = await api.delete<{ message: string }>(`/distributors/${distributorId}`)
  return data
}

export async function archiveDistributor(distributorId: string, archive = true) {
  const { data } = await api.patch<{ message: string; lifecycleStatus: string }>(
    `/distributors/${distributorId}/archive`,
    { archive },
  )
  return data
}

export async function markDistributorTargetObsolete(
  distributorId: string,
  targetId: string,
  isObsolete: boolean,
) {
  const { data } = await api.patch<{ message: string }>(
    `/distributors/${distributorId}/targets/${targetId}/obsolete`,
    { isObsolete },
  )
  return data
}

export async function fetchProductSourceOfTruthReadiness(productId: string) {
  const { data } = await api.get<ProductSoTReadinessDto>(`/products/${productId}/source-of-truth-readiness`)
  return data
}

export async function fetchProductsReadyForComparison() {
  const { data } = await api.get<ProductDto[]>('/frontend/products-ready-for-comparison')
  return data
}

export async function enqueuePendingDistributorScrapes(productId: string) {
  const { data } = await api.post<{ message: string; queued: number }>(
    `/products/${productId}/enqueue-pending-distributor-scrapes`,
  )
  return data
}

export async function confirmDistributorScrape(
  distributorId: string,
  body: DistributorConfirmScrapeRequest,
) {
  const { data } = await api.post<DistributorConfirmScrapeResponse>(
    `/distributors/${distributorId}/firecrawl-search/confirm`,
    body,
  )
  return data
}

// ── PDF Comparison Status ──────────────────────────────────────────────────

export interface PdfDifference {
  field: string
  canonical: string
  distributor: string
}

export interface PdfMismatchFinding {
  id: string
  status: string
  description: string
  distributorPdfUrl: string | null
  createdUtc: string
  differences: PdfDifference[] | null
}

export interface PdfDatasheetStatus {
  id: string
  url: string
  description: string | null
  hash: string | null
  hashStatus: string
  extractedTextBlobUri: string | null
  lastHashedUtc: string | null
  comparisonStatus: 'NotChecked' | 'HashOnly' | 'NoMismatches' | 'Mismatch' | 'Resolved'
  recentFindings: PdfMismatchFinding[]
}

export interface ProductPdfComparisonStatus {
  productId: string
  datasheets: PdfDatasheetStatus[]
}

export async function fetchProductPdfComparisonStatus(productId: string) {
  const { data } = await api.get<ProductPdfComparisonStatus>(`/products/${productId}/pdf-comparison-status`)
  return data
}

// Compliance workflows
export interface ComplianceWorkflowStepDto {
  id: string
  stepKey: string
  sortOrder: number
  title: string
  shortLabel: string
  description: string
  primaryRoutePath: string
  iconKey?: string | null
  routePatterns: string[]
}

export interface ComplianceWorkflowDto {
  id: string
  code: string
  name: string
  version: number
  isDefault: boolean
  isActive: boolean
  divisionId?: string | null
  subdivisionId?: string | null
  businessEntityId?: string | null
  steps: ComplianceWorkflowStepDto[]
}

export interface ComplianceWorkflowSummaryDto {
  id: string
  code: string
  name: string
  version: number
  isDefault: boolean
  isActive: boolean
  divisionId?: string | null
  subdivisionId?: string | null
  businessEntityId?: string | null
}

export interface ComplianceWorkflowUpdatePayload {
  name?: string
  isDefault?: boolean
  isActive?: boolean
  divisionId?: string | null
  subdivisionId?: string | null
  businessEntityId?: string | null
  clearDivision?: boolean
  clearSubdivision?: boolean
  clearBusinessEntity?: boolean
  steps?: {
    id?: string
    stepKey: string
    sortOrder: number
    title: string
    shortLabel: string
    description?: string
    primaryRoutePath: string
    iconKey?: string
    routePatterns?: string[]
  }[]
}

export async function fetchActiveComplianceWorkflow(params?: {
  businessEntityId?: string
  divisionId?: string
  subdivisionId?: string
}) {
  const { data } = await api.get<ComplianceWorkflowDto>('/compliance-workflows/active', { params: params ?? {} })
  return data
}

export async function fetchComplianceWorkflows() {
  const { data } = await api.get<ComplianceWorkflowSummaryDto[]>('/compliance-workflows')
  return data
}

export async function fetchComplianceWorkflow(id: string) {
  const { data } = await api.get<ComplianceWorkflowDto>(`/compliance-workflows/${id}`)
  return data
}

export async function createComplianceWorkflow(payload: {
  code?: string
  name?: string
  cloneFromWorkflowId?: string
  divisionId?: string
  subdivisionId?: string
  businessEntityId?: string
}) {
  const { data } = await api.post<ComplianceWorkflowDto>('/compliance-workflows', payload)
  return data
}

export async function updateComplianceWorkflow(id: string, payload: ComplianceWorkflowUpdatePayload) {
  const { data } = await api.put<ComplianceWorkflowDto>(`/compliance-workflows/${id}`, payload)
  return data
}

export async function publishComplianceWorkflow(id: string) {
  const { data } = await api.post<ComplianceWorkflowDto>(`/compliance-workflows/${id}/publish`)
  return data
}
